import React, { useMemo } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { scaleTime, scaleLinear } from '@visx/scale'
import { useTooltip } from "@visx/tooltip"
import { AreaClosed, LinePath } from '@visx/shape'
import { LinearGradient } from '@visx/gradient'
import { withParentSize } from '@visx/responsive'
import { curveLinear } from '@visx/curve'
import { Group } from '@visx/group'
import styles from './Graph.module.css'
import GraphTooltipBubble from './GraphTooltipBubble'
import GraphTooltipArea from './GraphTooltipArea'
import { getValue, getDate, getLayersExtents } from './graphUtils'
import { commonColors } from '../../utils/constants'
import { longNumberFormt } from '../../utils/tools'
import { AxisBottom, AxisLeft } from '@visx/axis'

const getColorByDiff = (diff) => diff < 0 ? commonColors.stockDown : commonColors.stockUp

function SparklineGraph({
    parentWidth: width,
    parentHeight: height,
    graph: originalGraphs,
    margin = { top: 0, bottom: 0, right: 0, left: 0 },
    showAxisBottom,
    showAxisLeft
}) {
    const tooltip = useTooltip()
    const { colors } = usePaletteTheme()
    const layers = useMemo(() => originalGraphs?.map(originalGraph => (
        {
            ...originalGraph,
            color: originalGraph.color ?? getColorByDiff(originalGraph.diff)
        }
    )),[originalGraphs])

    const axisBottomTickLabelProps = () => ({
        textAnchor: 'middle',
        fontFamily: 'Arial',
        fontSize: 10,
        fill: colors.textPrimary,
    })
    
    const axisLeftTickLabelProps = () => ({
        fontFamily: 'Arial',
        fontSize: 10,
        textAnchor: 'start',
        fill: colors.textPrimary,
    })

    // bounds
    const xMax = Math.max(width - margin.right - margin.left, 0)
    const yMax = Math.max(height - margin.top - margin.bottom, 0)
    const yMaxGraph = yMax - (showAxisBottom ? 50 : 0)

    const xScale = useMemo(() => {
        const extents = getLayersExtents(layers, getDate)
        return scaleTime({
            range: [0 + (showAxisLeft ? 50 : 0), xMax],
            domain: [extents[0], extents[1] || 0],
        })
    }, [xMax, layers, showAxisLeft],)
  
    const yScale = useMemo(() => {
        const extents = getLayersExtents(layers, getValue)
        return scaleLinear({
            range: [yMaxGraph, 0],
            domain: [extents[0], extents[1] || 0],
            nice: true,
        })
    }, [yMaxGraph, layers])

    const chart = useMemo(() => layers?.map(layer => (
        <Group key={Math.random()} left={margin.left} top={margin.top}>
        <LinearGradient
            id={`gradient-${layer.color}`}
            from={layer.color}
            fromOpacity={0.4}
            to={layer.color}
            toOpacity={0}
            {...layer.linearGradient}
        />
        <AreaClosed
            data={layer.data}
            x={d => xScale(getDate(d)) }
            y={d => yScale(getValue(d)) }
            yScale={yScale}
            fill={`url(#gradient-${layer.color})`}
            curve={curveLinear}
        />
        <LinePath
            data={layer.data}
            x={d => xScale(getDate(d)) }
            y={d => yScale(getValue(d)) }
            strokeWidth={1}
            stroke={layer.color}
            curve={curveLinear}
            {...layer.linePathProps}
        />
        </Group>
    )) ,[layers, xScale, yScale, margin])

    return (
        <div className={styles['relative']}>
            <svg width={width} height={height}>
                { chart }
                {showAxisLeft &&
                    <AxisLeft 
                        hideAxisLine
                        hideTicks
                        left={10}
                        scale={yScale}
                        tickLabelProps={axisLeftTickLabelProps}
                        tickFormat={longNumberFormt}
                        numTicks={5}
                    />
                }
                {showAxisBottom &&
                    <AxisBottom
                        hideAxisLine
                        hideTicks
                        top={yMax-40}
                        scale={xScale}
                        tickLabelProps={axisBottomTickLabelProps}
                    />
                }
                <GraphTooltipArea
                    width={xMax}
                    height={yMax}
                    xScale={xScale}
                    yScales={yScale}
                    layers={layers}
                    tooltip={tooltip}
                    margin={margin}
                />
            </svg>
            <GraphTooltipBubble
                width={xMax}
                height={yMax}
                xScale={xScale}
                axisDate={false}
                tooltip={tooltip}
                tooltipBounds={false}
                margin={margin}
            />
        </div>
    )
}

export default withParentSize(SparklineGraph)